import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { enumAttributeConverter } from '../enum-types';
import {
   type AlertVariant,
   AlertEnums,
   AtlInlineAlertProps,
   AlertTags,
   AlertEvents,
} from './inline-alert.types';
import { getGlobalStyleSheets } from '../../global-styles';
import { classMap } from 'lit/directives/class-map.js';

import InlineAlertStyles from './inline-alert.scss?inline';

import classNames from 'classnames';

@customElement(AlertTags.InlineAlert)
export class AtlInlineAlert extends LitElement implements AtlInlineAlertProps {
   @property({
      type: String,
      converter: enumAttributeConverter(
         AlertEnums.AlertVariant,
         AlertEnums.AlertVariant.Info
      ),
   })
   variant: AlertVariant;

   @property({ type: Boolean })
   showIcon?: boolean;

   @property({ type: String })
   iconName?: string;

   @property({ type: Boolean })
   dismissable?: boolean;

   @property({ type: String, attribute: 'alert-class' })
   alertClassName?: string;

   @property({ type: Boolean, state: true })
   _dismissing?: boolean;

   @property({ type: Boolean, state: true })
   _dismissed?: boolean;

   static styles = [...getGlobalStyleSheets(), InlineAlertStyles];

   renderIcon() {
      if (this.variant === AlertEnums.AlertVariant.Highlight) return nothing;

      if (this.showIcon) {
         return html`
            <i
               class="alert-icon fa fa-${this.iconName ||
               DefaultIconName[this.variant]}"
            ></i>
         `;
      }

      return nothing;
   }

   private _renderDismissable = () => {
      if (!this.dismissable) return nothing;

      return html`<button
         type="button"
         class="btn btn-close px-3 p-2"
         data-bs-dismiss="alert"
         aria-label="Close"
         @click=${this._handleDismissal}
      ></button>`;
   };

   render() {
      if (this._dismissed) return nothing;

      const alertBodyClasses = {
         'alert-body': true,
         mob: true,
         'align-items-center': true,
         [AlertClass[this.variant]]: true,
      };

      return html`
         <div class="${classNames('app-alert', this.alertClassName)}">
            <div
               role="alert"
               class=${classNames(
                  'alert',
                  'fade',
                  !this._dismissing ? 'show' : 'hide'
               )}
            >
               <div class=${classMap(alertBodyClasses)}>
                  <div class="alert-text align-items-center">
                     ${this.renderIcon()}
                     <div class="d-flex gap-2">
                        <slot></slot>
                     </div>
                  </div>
                  <slot name="sideContent"></slot>
                  ${this._renderDismissable()}
               </div>
            </div>
         </div>
      `;
   }

   private _handleDismissal = () => {
      this._dismissing = true;
      this.dispatchEvent(new AlertEvents.DismissAlertEvent());

      const alert = this.shadowRoot?.querySelector('.alert');
      alert?.addEventListener('transitionend', () => (this._dismissed = true));
   };
}

const DefaultIconName: Record<AlertVariant, string | undefined> = {
   [AlertEnums.AlertVariant.Success]: 'circle-check',
   [AlertEnums.AlertVariant.Neutral]: 'message',
   [AlertEnums.AlertVariant.Warning]: 'triangle-exclamation',
   [AlertEnums.AlertVariant.Info]: 'circle-info',
   [AlertEnums.AlertVariant.Error]: 'triangle-exclamation',
   [AlertEnums.AlertVariant.SuccessBordered]: 'circle-check',
   [AlertEnums.AlertVariant.NeutralBordered]: 'message',
   [AlertEnums.AlertVariant.WarningBordered]: 'triangle-exclamation',
   [AlertEnums.AlertVariant.InfoBordered]: 'circle-info',
   [AlertEnums.AlertVariant.ErrorBordered]: 'triangle-exclamation',
   [AlertEnums.AlertVariant.Highlight]: undefined,
};

const AlertClass: Record<AlertVariant, string> = {
   [AlertEnums.AlertVariant.Success]: 'alert-success',
   [AlertEnums.AlertVariant.Neutral]: 'alert-neutral',
   [AlertEnums.AlertVariant.Warning]: 'alert-warning',
   [AlertEnums.AlertVariant.Info]: 'alert-info',
   [AlertEnums.AlertVariant.Error]: 'alert-error',
   [AlertEnums.AlertVariant.SuccessBordered]: 'alert-success-bordered',
   [AlertEnums.AlertVariant.NeutralBordered]: 'alert-neutral-bordered',
   [AlertEnums.AlertVariant.WarningBordered]: 'alert-warning-bordered',
   [AlertEnums.AlertVariant.InfoBordered]: 'alert-info-bordered',
   [AlertEnums.AlertVariant.ErrorBordered]: 'alert-error-bordered',
   [AlertEnums.AlertVariant.Highlight]: 'alert-highlight',
};
